import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import Grid from "@mui/material/Grid";
import { Box, Typography } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDProgress from "components/MDProgress";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";
import { calcularPorcentagemDiferenca, formatCurrency } from "helpers/utils";
import type {
  DashboardEstatisticasResponse,
  OrcamentoEstatisticasResumo,
} from "services/estatisticasDashboard";
import StaticticsCardRegisterAtletas from "./StaticticsCardRegisterAtletas";
import StatisticsCardTotalEquipes from "./StatisticsCardTotalEquipes";

const DOUGHNUT_COLORS = [
  "#17c1e8",
  "#82d616",
  "#fbcf33",
  "#ea0606",
  "#344767",
  "#7928ca",
];

const FUNIL_COLORS = ["success", "warning", "error"] as const;

/** Altura compartilhada para pares de gráficos lado a lado. */
const PAIR_CHART_HEIGHT = "19.125rem";

function formatVariacao(atual: number, anterior: number) {
  return calcularPorcentagemDiferenca(anterior, atual);
}

function ocupacaoBarColor(
  percentual: number
): "info" | "success" | "warning" | "error" {
  if (percentual >= 90) return "error";
  if (percentual >= 70) return "warning";
  if (percentual >= 40) return "info";
  return "success";
}

/** Compensa mt={-5} dos charts Reports* e evita sobreposição entre linhas. */
function ChartRowWrap({ children }: { children: ReactNode }) {
  return (
    <MDBox sx={{ pt: { xs: 2, md: 5 }, pb: 1, height: "100%" }}>
      {children}
    </MDBox>
  );
}

function ChartGridItem({
  children,
  xs = 12,
  md = 6,
  lg,
}: {
  children: ReactNode;
  xs?: number;
  md?: number;
  lg?: number;
}) {
  return (
    <Grid item xs={xs} md={md} lg={lg ?? md} sx={{ display: "flex" }}>
      <MDBox
        sx={{
          flex: 1,
          width: "100%",
          minWidth: 0,
          "& .MuiCard-root": { height: "100%" },
        }}
      >
        <ChartRowWrap>{children}</ChartRowWrap>
      </MDBox>
    </Grid>
  );
}

type Props = {
  data: DashboardEstatisticasResponse;
  orcamento: OrcamentoEstatisticasResumo | null;
  showFinance: boolean;
  cardLg: 3 | 4;
};

export function SecaoEventos({ data, cardLg }: Props) {
  const { operacional, inscricoes, ocupacaoEventos } = data;
  const { porCanal } = inscricoes;
  const inscVar = formatVariacao(
    operacional.inscricoes.total,
    operacional.inscricoes.totalAnterior
  );

  const inscricoesChart = {
    labels: inscricoes.mensalYoy.labels,
    datasets: [
      {
        label: inscricoes.mensalYoy.anoAnteriorLabel,
        color: "light" as const,
        data: inscricoes.mensalYoy.anoAnterior,
      },
      {
        label: inscricoes.mensalYoy.anoAtualLabel,
        color: "success" as const,
        data: inscricoes.mensalYoy.anoAtual,
      },
    ],
  };

  const inscDiaChart = {
    labels: inscricoes.porDia.labels.slice(-15),
    datasets: [
      {
        label: "Inscrições",
        color: "info" as const,
        data: inscricoes.porDia.data.slice(-15),
      },
    ],
  };

  const funilTotal =
    operacional.funil.pago +
    operacional.funil.pendente +
    operacional.funil.cancelado;

  const canalChartLabels = [
    "Site",
    "App iOS",
    "App Android",
    ...(porCanal.appIndeterminado > 0 ? ["App (não identificado)"] : []),
    ...(porCanal.painel > 0 ? ["Painel"] : []),
  ];
  const canalChartData = [
    porCanal.site,
    porCanal.appIos,
    porCanal.appAndroid,
    ...(porCanal.appIndeterminado > 0 ? [porCanal.appIndeterminado] : []),
    ...(porCanal.painel > 0 ? [porCanal.painel] : []),
  ];
  const canalTotal = porCanal.site + porCanal.app + porCanal.painel;

  return (
    <>
      <Grid container spacing={3}>
        <StaticticsCardRegisterAtletas gridLg={cardLg} />
        <StatisticsCardTotalEquipes gridLg={cardLg} />
        <Grid item xs={12} md={6} lg={cardLg}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="info"
              icon="event"
              title="Eventos abertos"
              count={operacional.eventos.abertos}
              percentage={{
                color: "success",
                amount: operacional.eventos.emProcessamento,
                label: "em processamento",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={cardLg}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="dark"
              icon="event_busy"
              title="Eventos encerrados"
              count={operacional.eventos.encerrados}
              percentage={{
                color: "success",
                amount: "",
                label: "Total histórico",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={cardLg}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="primary"
              icon="how_to_reg"
              title="Inscrições no período"
              count={operacional.inscricoes.total}
              percentage={{
                color: inscVar.variacaoPositiva ? "success" : "error",
                amount: inscVar.percentageFormated,
                label: "vs período anterior",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={cardLg}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="info"
              icon="language"
              title="Inscrições via site"
              count={porCanal.site}
              percentage={{
                color: "success",
                amount: "",
                label: "Inscrições antigas sem canal = Site",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={cardLg}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="success"
              icon="smartphone"
              title="Inscrições via app"
              count={porCanal.app}
              percentage={{
                color: "success",
                amount: `${porCanal.appIos} iOS · ${porCanal.appAndroid} Android`,
                label: "Total mobile no período",
              }}
            />
          </MDBox>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <MDBox sx={{ height: "100%", pb: 1 }}>
            <DefaultDoughnutChart
              icon={{ color: "primary", component: "devices" }}
              title="Inscrições por canal"
              description={`Total: ${canalTotal} no período · inscrições antigas agrupadas em Site`}
              height="14rem"
              chart={{
                labels: canalChartLabels,
                datasets: {
                  label: "Inscrições",
                  backgroundColors: DOUGHNUT_COLORS.slice(
                    0,
                    canalChartLabels.length
                  ),
                  data: canalChartData,
                },
                cutout: 60,
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6}>
          <MDBox sx={{ height: "100%", pb: 1 }}>
            <DefaultDoughnutChart
              icon={{ color: "info", component: "donut_large" }}
              title="Funil de inscrições"
              description={`Total: ${funilTotal} no período selecionado`}
              height="14rem"
              chart={{
                labels: ["Pagas/Confirmadas", "Pendentes", "Canceladas"],
                datasets: {
                  label: "Inscrições",
                  backgroundColors: [...FUNIL_COLORS],
                  data: [
                    operacional.funil.pago,
                    operacional.funil.pendente,
                    operacional.funil.cancelado,
                  ],
                },
                cutout: 60,
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6}>
          {inscricoes.porModalidade.length > 0 && (
            <MDBox sx={{ height: "100%", pb: 1 }}>
              <HorizontalBarChart
                icon={{ color: "success", component: "directions_run" }}
                title="Inscrições por modalidade"
                description="Top modalidades no período"
                height="14rem"
                chart={{
                  labels: inscricoes.porModalidade.map((m) => m.modalidade),
                  datasets: [
                    {
                      label: "Inscrições",
                      color: "success" as const,
                      data: inscricoes.porModalidade.map((m) => m.qtd),
                    },
                  ],
                }}
              />
            </MDBox>
          )}
        </Grid>

        <ChartGridItem lg={6}>
          <ReportsBarChart
            color="info"
            title="Inscrições por dia"
            description="Últimos 15 dias com movimento"
            chart={inscDiaChart}
          />
        </ChartGridItem>
        <ChartGridItem lg={6}>
          <ReportsLineChart
            color="success"
            title="Inscrições por mês"
            description="Comparativo ano atual vs anterior"
            chart={inscricoesChart}
          />
        </ChartGridItem>
      </Grid>

      {ocupacaoEventos.length > 0 && (
        <MDBox mt={4}>
          <MDTypography variant="h6" mb={2}>
            Ocupação — eventos com inscrições abertas
          </MDTypography>
          <Grid container spacing={2}>
            {ocupacaoEventos.map((ev) => (
              <Grid item xs={12} md={6} key={ev.slug}>
                <MDBox
                  p={2}
                  borderRadius="lg"
                  sx={{
                    border: "1px solid",
                    borderColor: "grey.200",
                    bgcolor: "background.paper",
                  }}
                >
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1}
                    mb={1}
                  >
                    <MDTypography variant="button" fontWeight="bold">
                      {ev.titulo}
                    </MDTypography>
                    <MDButton
                      component={Link}
                      to={`/layouts/pages/cadastros/eventos/${ev.slug}`}
                      variant="text"
                      color="info"
                      size="small"
                    >
                      Ver
                    </MDButton>
                  </MDBox>
                  <Typography variant="caption" color="text.secondary">
                    {ev.inscritos} / {ev.limite} inscritos (
                    {ev.percentual.toFixed(1)}%)
                  </Typography>
                  <MDProgress
                    variant="gradient"
                    color={ocupacaoBarColor(ev.percentual)}
                    value={ev.percentual}
                    sx={{ mt: 1, height: 8, borderRadius: 1 }}
                  />
                </MDBox>
              </Grid>
            ))}
          </Grid>
        </MDBox>
      )}

      <MDBox mt={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <RankingList
              title="Top cidades (período)"
              subtitle="Inscrições por cidade do atleta"
              items={inscricoes.porCidade.map((c) => ({
                label: c.cidade,
                value: c.qtd,
              }))}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RankingList
              title="Top equipes (período)"
              subtitle="Inscrições por equipe do atleta"
              items={inscricoes.porEquipe.map((e) => ({
                label: e.equipe,
                value: e.qtd,
              }))}
            />
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
}

export function SecaoFinanceiro({ data }: Props) {
  const { receita, financeiro } = data;
  const recVar = formatVariacao(receita.ytd.atual, receita.ytd.anterior);

  const receitaChart = {
    labels: receita.mensalYoy.labels,
    datasets: [
      {
        label: receita.mensalYoy.anoAnteriorLabel,
        color: "light" as const,
        data: receita.mensalYoy.anoAnterior,
      },
      {
        label: receita.mensalYoy.anoAtualLabel,
        color: "warning" as const,
        data: receita.mensalYoy.anoAtual,
      },
    ],
  };

  const pagamentoLabels = receita.porTipoPagamento.map((p) => p.tipo);
  const pagamentoData = receita.porTipoPagamento.map((p) => p.total);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="success"
              icon="paid"
              title="Receita acumulada"
              count={formatCurrency(receita.ytd.atual)}
              percentage={{
                color: recVar.variacaoPositiva ? "success" : "error",
                amount: recVar.percentageFormated,
                label: `vs mesmo período (${receita.ytd.periodoLabel})`,
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="info"
              icon="receipt_long"
              title="Ticket médio inscrições"
              count={formatCurrency(financeiro.ticketMedioInscricoes)}
              percentage={{
                color: "success",
                amount: "",
                label: "Pagamentos confirmados",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="primary"
              icon="account_balance_wallet"
              title="A receber (aberto)"
              count={formatCurrency(financeiro.receberAberto)}
              percentage={{
                color: "success",
                amount: "",
                label: "Valor em aberto",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="error"
              icon="warning"
              title="Receber vencido"
              count={formatCurrency(financeiro.receberVencido)}
              percentage={{
                color: "error",
                amount: "",
                label: "Vencidos em aberto",
              }}
            />
          </MDBox>
        </Grid>
      </Grid>

      <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
        <MDButton
          component={Link}
          to="/loja/financeiro/contas-receber"
          variant="outlined"
          color="info"
          size="small"
        >
          Contas a receber
        </MDButton>
        <MDButton
          component={Link}
          to="/financeiro/financeiro-eventos"
          variant="outlined"
          color="dark"
          size="small"
        >
          Financeiro por evento
        </MDButton>
        <MDButton
          component={Link}
          to="/loja/financeiro/dre-gerencial"
          variant="outlined"
          color="dark"
          size="small"
        >
          DRE gerencial
        </MDButton>
      </MDBox>

      <Grid container spacing={3} sx={{ mt: 4, alignItems: "stretch" }}>
        <ChartGridItem md={pagamentoLabels.length > 0 ? 6 : 12}>
          <VerticalBarChart
            icon={{ color: "dark", component: "leaderboard" }}
            title="Receita mensal"
            description="Pagamentos baixados — comparativo anual"
            height={PAIR_CHART_HEIGHT}
            chart={receitaChart}
          />
        </ChartGridItem>
        {pagamentoLabels.length > 0 && (
          <ChartGridItem md={6}>
            <DefaultDoughnutChart
              icon={{ color: "warning", component: "payments" }}
              title="Receita por forma de pagamento"
              description="Distribuição no período"
              height={PAIR_CHART_HEIGHT}
              chart={{
                labels: pagamentoLabels,
                datasets: {
                  label: "Receita",
                  backgroundColors: DOUGHNUT_COLORS,
                  data: pagamentoData,
                },
                cutout: 55,
              }}
            />
          </ChartGridItem>
        )}
      </Grid>
    </>
  );
}

export function SecaoLoja({ data }: Props) {
  const { loja, receita } = data;
  const lojaVar = formatVariacao(
    loja.faturamentoLiquido,
    loja.faturamentoAnterior
  );

  const eventosVsLojaChart = {
    labels: receita.eventosVsLoja.labels,
    datasets: [
      {
        label: "Eventos",
        color: "info" as const,
        data: receita.eventosVsLoja.eventos,
      },
      {
        label: "Loja",
        color: "warning" as const,
        data: receita.eventosVsLoja.loja,
      },
    ],
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="success"
              icon="store"
              title="Faturamento loja"
              count={formatCurrency(loja.faturamentoLiquido)}
              percentage={{
                color: lojaVar.variacaoPositiva ? "success" : "error",
                amount: lojaVar.percentageFormated,
                label: "vs período anterior",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="info"
              icon="shopping_bag"
              title="Pedidos pagos"
              count={loja.pedidosPagos}
              percentage={{
                color: "success",
                amount: "",
                label: "No período selecionado",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="dark"
              icon="sell"
              title="Ticket médio loja"
              count={formatCurrency(loja.ticketMedio)}
              percentage={{
                color: "success",
                amount: "",
                label: "Pedidos confirmados",
              }}
            />
          </MDBox>
        </Grid>
      </Grid>

      <MDBox mt={2}>
        <MDButton
          component={Link}
          to="/loja/pedidos"
          variant="outlined"
          color="info"
          size="small"
        >
          Ver pedidos
        </MDButton>
        <MDButton
          component={Link}
          to="/loja/financeiro"
          variant="outlined"
          color="dark"
          size="small"
          sx={{ ml: 1 }}
        >
          Dashboard financeiro
        </MDButton>
      </MDBox>

      <MDBox sx={{ mt: 4, mb: 2 }}>
        <ChartRowWrap>
          <VerticalBarChart
            icon={{ color: "info", component: "stacked_bar_chart" }}
            title="Receita eventos vs loja (mensal YTD)"
            description="Comparativo de faturamento por origem"
            height="19rem"
            chart={eventosVsLojaChart}
          />
        </ChartRowWrap>
      </MDBox>
    </>
  );
}

export function SecaoOrcamentos({
  orcamento,
}: {
  orcamento: OrcamentoEstatisticasResumo | null;
}) {
  if (!orcamento) {
    return (
      <MDTypography variant="body2" color="text">
        Não foi possível carregar dados de orçamentos.
      </MDTypography>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="info"
              icon="request_quote"
              title="Solicitações abertas"
              count={orcamento.abertas}
              percentage={{
                color: "success",
                amount: "",
                label: "Pipeline comercial",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="success"
              icon="trending_up"
              title="Valor em pipeline"
              count={formatCurrency(orcamento.pipelineValor)}
              percentage={{
                color: "success",
                amount: "",
                label: "Propostas em andamento",
              }}
            />
          </MDBox>
        </Grid>
      </Grid>

      <MDBox mt={2}>
        <MDButton
          component={Link}
          to="/pages/orcamento/solicitacoes"
          variant="outlined"
          color="info"
          size="small"
        >
          Ver solicitações
        </MDButton>
      </MDBox>

      {(orcamento.porStatus ?? []).length > 0 && (
        <MDBox sx={{ mt: 4, mb: 2 }}>
          <HorizontalBarChart
            icon={{ color: "dark", component: "assignment" }}
            title="Solicitações por status"
            description="Funil de orçamentos"
            height="16rem"
            chart={{
              labels: (orcamento.porStatus ?? []).map((s) => s.label),
              datasets: [
                {
                  label: "Quantidade",
                  color: "dark" as const,
                  data: (orcamento.porStatus ?? []).map((s) => s.qtd),
                },
              ],
            }}
          />
        </MDBox>
      )}
    </>
  );
}

function RankingList({
  title,
  subtitle,
  items,
}: {
  title: string;
  subtitle: string;
  items: { label: string; value: number }[];
}) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 2,
        p: 2,
        height: "100%",
      }}
    >
      <MDTypography variant="h6">{title}</MDTypography>
      <MDTypography variant="caption" color="text" display="block" mb={2}>
        {subtitle}
      </MDTypography>
      <MDBox sx={{ maxHeight: 280, overflowY: "auto" }}>
        {items.length === 0 ? (
          <MDTypography variant="caption" color="text">
            Sem dados no período.
          </MDTypography>
        ) : (
          items.map((item, idx) => (
            <MDBox
              key={`${item.label}-${idx}`}
              display="flex"
              justifyContent="space-between"
              py={0.75}
              px={1}
              sx={{
                bgcolor: idx % 2 === 0 ? "grey.100" : "grey.50",
                borderRadius: 1,
              }}
            >
              <Typography variant="caption">{item.label}</Typography>
              <Typography variant="caption" fontWeight="bold">
                {item.value}
              </Typography>
            </MDBox>
          ))
        )}
      </MDBox>
    </Box>
  );
}
